import { getOwner } from "@ember/application"
import Component from "@ember/component"
import { action, computed, get, set } from "@ember/object"
import { later, scheduleOnce } from "@ember/runloop"
import { inject as service } from "@ember/service"
import { A, pipe } from "@mobily/ts-belt"
import {
  ALL_SCHOOL_YEAR_IDS,
  COPIED_PLANBOOK_DEFAULT_SCHOOL_YEAR_ID,
  CREATE_PLANBOOK_SCHOOL_YEAR_OPTIONS,
} from "@shared/models/school-year"
import type { User } from "@shared/models/user"
import findDocument from "cc-frontend/helpers/find-document"
import findMany from "cc-frontend/helpers/find-many"
import hasManyCollectively from "cc-frontend/helpers/has-many-collectively"
import type EmberStore from "cc-frontend/services/ember-store"
import type ModalManager from "cc-frontend/services/modal-manager"
import type Notifier from "cc-frontend/services/notifier"
import type QueryService from "cc-frontend/services/query"
import type Rpc from "cc-frontend/services/rpc"
import { fibonacciBackoff } from "cc-frontend/services/socket"
import { isCachedMembershipActiveInclAncestors } from "cc-frontend/utils/group"
import fade from "ember-animated/transitions/fade"
import { toDown, toLeft, toRight, toUp } from "ember-animated/transitions/move-over"
import _ from "lodash"
import { concat, forEach, includes, isNil, without } from "lodash-es"
import { tracked } from "tracked-built-ins"

import type Router from "cc-frontend/router"
import styles from "./modal.module.css"

/** Output of importable_planbooks_for_user for one planbook. */
interface PlanbookView {
  id: string
  title: string
  schoolYearId: string
  ownerFullName: string
  ownerId: string
  ownerFirstName: string
  ownerLastName: string
  ownerInitials: string
  cover: string
  courses: {
    id: string
    title: string | null
    color: string | null
  }
}

let headerNameStack: Array<string> = ["CHOOSE_PLANBOOK"]
let bodyNameStack: Array<string> = ["CHOOSE_PLANBOOK"]

export default class CopyPlanbookModalComponent extends Component {
  readonly styles = styles

  // args
  model!: {
    user: User
  }

  closeModal!: Function

  readonly createPlanbookSchoolYearOptions = CREATE_PLANBOOK_SCHOOL_YEAR_OPTIONS.filter(
    // Copying to summer doesn't appear to be sorted by the backend.
    (option) => option.season === "FULL_YEAR"
  )

  // state
  currentScreen = "CHOOSE_PLANBOOK"
  fade = fade
  planbooksQuery
  isFetchingQuery = false
  planbooksQueryResult: PlanbookView[] = []
  queryAttempts = 0
  newPlanbookTitle: string | null = null
  /** schoolYearId of the *source* planbook. */
  selectedSchoolYearId: string = COPIED_PLANBOOK_DEFAULT_SCHOOL_YEAR_ID
  selectedPlanbookId: string | null = null
  schoolAndNetworkGroupIds: null | Array<string> = null
  groupRoles: Record<string, string> = {}
  availableRoles = [
    { id: "planbook:is-commenter", title: "Can Comment" },
    { id: "planbook:is-editor", title: "Can Edit" },
  ]
  shouldCopy: boolean | null = null

  @tracked isCopying = false
  /** Option ID of the *new* planbook's school year. */
  @tracked newPlanbookYearOptionId: string | null = null
  /**
   * These hold the course ids the user DOESN'T want to copy.
   * This is for two reasons: 1) async. It would be more code to find the courseIds
   * and then assign them. 2) we're assuming they want them all, so it's just easy
   * to get what they don't want.
   */
  unselectedCourseIds: Array<string> = []

  // services
  @service("ember-store") declare store: EmberStore
  @service declare query: QueryService
  @service declare rpc: Rpc
  @service declare modalManager: ModalManager
  @service declare notifier: Notifier

  didReceiveAttrs() {
    if (isNil(this.model) || isNil(this.model.user)) return
    let queryId = this.model.user.id
    let planbooksQuery = this.query.generateLiveQuery(
      "importable_planbooks_for_user",
      queryId,
      () => {
        this.fetchQuery()
      }
    )
    set(this, "planbooksQuery", planbooksQuery)

    scheduleOnce("afterRender", this, () => {
      set(this, "isFetchingQuery", true)
      this.fetchQuery()
    })

    // Set up the school and network groups
    if (isNil(this.schoolAndNetworkGroupIds) && this.model.user) {
      const schoolAndNetworkGroupIds = pipe(
        this.model.user.attributes.cachedMemberships,
        A.filter(isCachedMembershipActiveInclAncestors),
        A.filter(
          (membership) => membership.usedAs === "school" || membership.usedAs === "school-network"
        ),
        A.map((membership) => membership.groupId),
        A.uniq
      )

      forEach(schoolAndNetworkGroupIds, (id) => {
        this.groupRoles[id] = "planbook:is-editor"
      })
      this.set("schoolAndNetworkGroupIds", schoolAndNetworkGroupIds)
    }
  }

  fetchQuery() {
    if (this.isDestroyed || this.isDestroying) return
    if (this.model.user.id === undefined) return

    get(this, "query")
      .fetch(this.planbooksQuery, 0, 200)
      .then((response) => {
        if (this.isDestroyed || this.isDestroying) return
        set(this, "planbooksQueryResult", response.data.results.planbooks)
        set(this, "isFetchingQuery", false)
      })
      .catch((error) => {
        this.queryAttempts++
        let newDelay = fibonacciBackoff(this.queryAttempts, 1000, 60000)
        console.error(
          `FAILED_QUERY #${this.queryAttempts}: ${this.planbooksQuery.type}:${this.planbooksQuery.id}`,
          error
        )
        later(() => this.fetchQuery(), newDelay)
      })
  }

  schoolYearIds = ALL_SCHOOL_YEAR_IDS

  @findMany("schoolYearIds", "school-year") schoolYears
  @findDocument("selectedPlanbookId", "planbook") selectedPlanbook
  @hasManyCollectively("selectedPlanbook", "course", "courses")
  courses

  @findMany("schoolAndNetworkGroupIds", "group") schoolsAndNetworks

  @computed("schoolYears.isFulfilled", "selectedSchoolYearId")
  get selectedSchoolYear() {
    if (this.schoolYears.isPending) return null
    return _.find(this.schoolYears.content, (el) => {
      return get(el, "id") === this.selectedSchoolYearId
    })
  }

  @computed("isFetchingQuery", "planbooksQueryResult.length", "selectedSchoolYearId")
  get planbooksForSchoolYear(): Array<{
    ownerIsUser: boolean
    ownerFirstName: string
    ownerLastName: string
    ownerInitials: string
    planbooks: PlanbookView[]
  }> {
    if (this.isFetchingQuery === true) return []
    return pipe(
      this.planbooksQueryResult,
      A.filter((planbook) => planbook.schoolYearId === this.selectedSchoolYearId),
      A.sortBy((planbook) => {
        // Put your planbooks at the top
        if (planbook.ownerId === this.model.user.id) {
          return "A1"
        } else {
          return planbook.ownerLastName
        }
      }),
      A.groupBy((planbook) => planbook.ownerId),
      (grouped) => Object.entries(grouped),
      A.map(([ownerId, planbookViews]) => {
        return {
          ownerIsUser: ownerId === this.model.user.id,
          ownerFirstName: planbookViews![0].ownerFirstName,
          ownerLastName: planbookViews![0].ownerLastName,
          ownerInitials: planbookViews![0].ownerInitials,
          planbooks: A.sortBy(
            planbookViews as readonly PlanbookView[] as PlanbookView[],
            (planbookView) => planbookView.title
          ),
        }
      })
    )
  }

  get userPlanbooksForSchoolYear() {
    return A.filter(this.planbooksForSchoolYear, (el) => el.ownerIsUser)
  }

  get otherPlanbooksForSchoolYear() {
    return A.filter(this.planbooksForSchoolYear, (el) => el.ownerIsUser === false)
  }

  headerAnimationRules({ oldItems, newItems }) {
    let newVal = newItems ? newItems[0] : null
    let oldVal = oldItems ? oldItems[0] : null
    if (oldVal === undefined || oldVal === null) return toDown
    if (newVal === undefined || newVal === null) return toUp
    let newComponentName = newVal
    let oldComponentName = oldVal

    // we're going backwards
    if (
      _.includes(headerNameStack, newComponentName) &&
      _.includes(headerNameStack, oldComponentName)
    ) {
      _.pull(headerNameStack, oldComponentName)
      return toDown
    } else {
      headerNameStack.push(newComponentName)
      return toUp
    }
  }

  bodyAnimationRules({ oldItems, newItems }) {
    let newVal = newItems ? newItems[0] : null
    let oldVal = oldItems ? oldItems[0] : null
    if (oldVal === undefined || oldVal === null) return toRight
    if (newVal === undefined || newVal === null) return toLeft
    let newComponentName = newVal
    let oldComponentName = oldVal

    // we're going backwards
    if (
      _.includes(bodyNameStack, newComponentName) &&
      _.includes(bodyNameStack, oldComponentName)
    ) {
      _.pull(bodyNameStack, oldComponentName)
      return toRight
    } else {
      bodyNameStack.push(newComponentName)
      return toLeft
    }
  }

  @action
  backToScreen(screenName) {
    this.set("currentScreen", screenName)
  }

  @action
  goToScreen(screenName) {
    this.set("currentScreen", screenName)
    // if (screenName === "ADD_TITLE"){
    //   scheduleOnce('afterRender', this, () => {
    //     this.
    //   })
    // }
  }

  @action
  selectSchoolYear(schoolYear) {
    this.set("selectedSchoolYearId", schoolYear.id)
  }

  @action
  selectPlanbook(planbookView) {
    this.set("selectedPlanbookId", planbookView.id)
    this.set("currentScreen", "CHOOSE_CLASSES")
  }

  @action
  toggleCourse(courseId) {
    if (includes(this.unselectedCourseIds, courseId)) {
      this.set("unselectedCourseIds", without(this.unselectedCourseIds, courseId))
    } else {
      this.set("unselectedCourseIds", concat(this.unselectedCourseIds, [courseId]))
    }
  }

  didInsertTitle(element: HTMLElement) {
    scheduleOnce("afterRender", this, () => {
      if (element && element.firstElementChild) {
        ;(element.firstElementChild as HTMLElement).focus()
      }
    })
  }

  @action
  askToCopyButton(result) {
    let shouldCopy = result === "COPY_LESSONS"
    this.set("shouldCopy", shouldCopy)
    this.set("currentScreen", "CONFIRMATION")
  }

  @action
  setGroupRole(groupId, roleMap) {
    // @ts-ignore
    this.set(`groupRoles.${groupId}`, roleMap.id)
  }

  @action
  copyPlanbook() {
    if (this.isCopying) return
    let collaborators = _.map(this.groupRoles, (val, key) => {
      return {
        groupId: key,
        role: val,
      }
    })
    this.isCopying = true
    let courseIds = _.chain(this.courses.content)
      .map((course) => course.id)
      .reject((id) => _.includes(this.unselectedCourseIds, id))
      .value()
    let { schoolYearId, season } = this.createPlanbookSchoolYearOptions.find(
      (option) => option.id === this.newPlanbookYearOptionId
    )!
    this.rpc
      .execute("planbookcopier_copy_planbook", {
        collaborators: collaborators,
        ownerId: this.model.user.id,
        parentPlanbookId: this.selectedPlanbookId,
        schoolYearId,
        season,
        selectedCourseIds: courseIds,
        title: this.newPlanbookTitle || "",
        wantsToCopyLessons: this.shouldCopy,
      })
      .then((planbook) => {
        if (!planbook?.id) {
          // RPC returned ok: false.
          this.notifier.warning("", {
            title: "Error copying planbook",
            message: "Please try again shortly!",
            duration: 5000,
          })
          return
        }

        this.closeModal()
        ;(getOwner(this)!.lookup("router:main") as Router).transitionTo(
          "planbookv5.planner.import-lessons",
          planbook.id
        )
      })
      .finally(() => {
        this.isCopying = false
      })
  }
}
